<template>
  <div class="recognize-shell">
    <!-- 左侧：图片；右侧：行号 + 列号 合并为同一「映射配置」模块 -->
    <section class="recognize-panel">
      <header class="recognize-panel__head">
        <div class="head-left">
          <h3 class="recognize-panel__title">图片与表格映射</h3>
          <p class="recognize-panel__hint">左侧上传电价表截图；右侧上行号、列号分两列并排配置</p>
        </div>
        <div class="head-actions">
          <el-button class="meter-feature-btn meter-feature-btn--sm" type="primary" size="small" :loading="priceLoading" @click="handleFetchPrice">
            配置获取电价
          </el-button>
        </div>
      </header>
      <div class="main-row">
        <div class="left-container">
          <div class="img-container">
            <el-upload
              class="img-uploader"
              drag
              action="#"
              :http-request="() => {}"
              :show-file-list="false"
              :on-change="onImgChange"
              :before-upload="onBeforeUpload"
            >
              <div v-if="formData.imgUrl" class="preview-container">
                <img :src="formData.imgUrl" @click="onPreview" class="elec-price-img" />
              </div>
              <div v-else class="icon-info-container">
                <el-icon class="uploader-icon"><Plus /></el-icon>
                <div class="tip-text">{{ $str.getKey('收益管理/上传电价图片, 或手动拖拽到此区域') }}</div>
              </div>
            </el-upload>
          </div>
          <div v-if="formData.imgUrl" class="tip-container">
            <el-icon><InfoFilled /></el-icon> {{ $str.getKey('收益管理/可将电价图拖拽到图片区域进行更新') }}
          </div>
        </div>

        <div class="right-stack">
          <div class="right-card right-card--mapping">
            <div class="mapping-split">
              <div class="mapping-split__rows">
                <div class="recognize-subtitle">字段所在行</div>
                <div class="rows-grid rows-grid--in-top">
                  <div class="rows-module">
                    <div class="row-subtitle">大工业两部制</div>
                    <el-form :model="formData.optionFormData" class="options-grid options-grid--rows" label-width="52px">
                      <template v-for="item in rowItemsBigIndustry" :key="item.key">
                        <el-form-item :label="item.shortLabel" :prop="item.key">
                          <el-input-number v-model="formData.optionFormData[item.key]" :min="0" :controls="false" />
                        </el-form-item>
                      </template>
                    </el-form>
                  </div>

                  <div class="rows-module">
                    <div class="row-subtitle">一般工商业两部制</div>
                    <el-form class="options-grid options-grid--rows" label-width="52px">
                      <template v-for="item in rowItemsGeneralBusiness" :key="`gb-two-${item.key}`">
                        <el-form-item :label="item.shortLabel">
                          <el-input-number v-model="formData.optionFormData[item.key]" :min="0" :controls="false" />
                        </el-form-item>
                      </template>
                    </el-form>
                  </div>

                  <div class="rows-module">
                    <div class="row-subtitle">一般工商业单一制</div>
                    <el-form :model="formData.optionFormData" class="options-grid options-grid--rows" label-width="52px">
                      <template v-for="item in rowItemsGeneralBusinessSingle" :key="`gb-single-${item.key}`">
                        <el-form-item :label="item.shortLabel" :prop="item.key">
                          <el-input-number v-model="formData.optionFormData[item.key]" :min="0" :controls="false" />
                        </el-form-item>
                      </template>
                    </el-form>
                  </div>
                </div>
              </div>

              <div class="mapping-split__cols">
                <div class="recognize-subtitle">字段所在列</div>
                <div class="rows-module cols-module">
                  <div class="cols-title">尖峰平谷时段电价</div>
                  <el-form
                    ref="formElRef"
                    :model="formData.optionFormData"
                    class="options-grid options-grid--cols-stack"
                    label-width="52px"
                  >
                    <template v-for="item in columnItems" :key="item.key">
                      <el-form-item :label="getColumnLabel(item.key, item.name)" :prop="item.key">
                        <el-select
                          v-if="item.key === 'priceUnit'"
                          class="unit-select"
                          v-model="formData.optionFormData[item.key]"
                          :teleported="true"
                          placement="bottom-start"
                        >
                          <el-option v-for="unit in unitOptions" :key="unit.value" :label="unit.label" :value="unit.value" />
                        </el-select>
                        <el-input-number v-else v-model="formData.optionFormData[item.key]" :min="0" :controls="false" />
                      </el-form-item>
                    </template>
                  </el-form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ② 预览 -->
    <section class="recognize-panel recognize-panel--preview">
      <div class="preview-head">
        <div>
          <h3 class="recognize-panel__title recognize-panel__title--inline">效果预览</h3>
          <p class="recognize-panel__hint recognize-panel__hint--tight">根据行、列配置生成的时段电价示意</p>
        </div>
        <div class="preview-actions">
          <el-button class="meter-feature-btn meter-feature-btn--sm" type="primary" size="small" :loading="segmentLoading" @click="$emit('fetchSegments')">
            获取时段
          </el-button>
        </div>
      </div>
      <div class="preview-panel">
        <div v-if="previewOptions.length" class="preview-charts">
          <div v-for="(opt, i) in previewOptions" :key="i" class="preview-chart-item">
            <div v-if="previewTitles[i]" class="preview-chart-title">{{ previewTitles[i] }}</div>
            <VChart class="preview-chart" :option="opt" autoresize />
          </div>
        </div>
        <VChart v-else class="preview-chart" :option="previewOption" autoresize />
      </div>
    </section>
  </div>
</template>

<script setup>
import { watch, ref } from 'vue'
import { Plus, InfoFilled } from '@element-plus/icons-vue'
import VChart from 'vue-echarts'

const $str = window.$str

defineEmits(['fetchSegments', 'preview'])

const props = defineProps({
  formData: { type: Object, required: true },
  rowItemsBigIndustry: { type: Array, default: () => [] },
  rowItemsGeneralBusiness: { type: Array, default: () => [] },
  rowItemsGeneralBusinessSingle: { type: Array, default: () => [] },
  columnItems: { type: Array, default: () => [] },
  unitOptions: { type: Array, default: () => [] },
  segmentLoading: { type: Boolean, default: false },
  priceLoading: { type: Boolean, default: false },
  previewOption: { type: Object, required: true },
  /** 多套时段规则时：渲染多个图表（优先使用该字段） */
  previewOptions: { type: Array, default: () => [] },
  /** 对应每个图表的标题（可选） */
  previewTitles: { type: Array, default: () => [] },
  onImgChange: { type: Function, required: true },
  onBeforeUpload: { type: Function, required: true },
  onPreview: { type: Function, required: true },
  optionsFormRef: { type: Object, required: true },
  onFetchPrice: { type: Function, required: false },
})

function handleFetchPrice() {
  props.onFetchPrice?.()
}

/** 父组件 optionsForm.validate() 依赖此表单实例 */
const formElRef = ref(null)
watch(
  formElRef,
  (el) => {
    if (props.optionsFormRef) {
      props.optionsFormRef.value = el
    }
  },
  { immediate: true },
)

const getColumnLabel = (key, fallback) => {
  const k = String(key || '')
  if (k === 'sharpColumns') return '尖'
  if (k === 'peakColumns') return '峰'
  if (k === 'flatColumns') return '平'
  if (k === 'valleyColumns') return '谷'
  if (k === 'deepColumns') return '深'
  if (k === 'priceUnit') return '单位'
  return fallback || ''
}
</script>

<style scoped lang="scss">
@import '../../_shared/feature-button.scss';

.recognize-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 2px 4px;
}

/* 与 SpotPricePanel `.spot-list-panel` 同系 */
.recognize-panel {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  padding: 14px 16px 16px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.recognize-panel__head {
  margin-bottom: 10px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.head-left {
  min-width: 0;
}

.head-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.recognize-panel__title {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #2e2e2e;
}

.recognize-panel__title--inline {
  margin-bottom: 2px;
}

.recognize-panel__hint {
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(46, 46, 46, 0.52);
}

.recognize-panel__hint--tight {
  margin-top: 2px;
}

.recognize-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: rgba(46, 46, 46, 0.75);
  margin-bottom: 6px;
}

/* 左侧上传略窄，右侧映射区更宽，整体更均衡 */
.main-row {
  display: grid;
  grid-template-columns: minmax(260px, 0.85fr) minmax(320px, 1.15fr);
  gap: 14px;
  align-items: stretch;
}

.left-container {
  width: 100%;
  max-width: 520px;
  min-width: 0;
  align-self: stretch;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f5f7fb;
  border: 1px dashed rgba(58, 104, 255, 0.22);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.img-uploader {
  height: var(--upload-h, clamp(188px, 28vw, 292px));
  width: 100%;
  max-height: 340px;
}

.img-container :deep(.el-upload.el-upload--text) {
  width: 100%;
  height: 100%;
}

.img-container :deep(.el-upload-dragger) {
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-info-container {
  width: 100%;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  padding: 12px 8px;
  box-sizing: border-box;
}

.uploader-icon {
  font-size: 28px;
  color: rgba(58, 104, 255, 0.7);
}

.tip-text {
  font-size: 13px;
  color: rgba(46, 46, 46, 0.48);
  text-align: center;
  line-height: 1.45;
  padding: 0 20px;
}

.preview-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.elec-price-img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.right-stack {
  width: 100%;
  min-width: 0;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.right-card {
  border-radius: 10px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  overflow-x: hidden;
}

/* 右侧合并：高度随内容，不强制 min-height，避免大块空白 */
.right-card--mapping {
  gap: 0;
  padding: 14px 16px 16px;
  height: auto;
  flex: 1;
  min-height: 0;
  overflow: visible;
}

/* 行区吃主要宽度；列区与行块同款卡片，固定窄条 */
.mapping-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px 14px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

.mapping-split__rows {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mapping-split__cols {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 166px;
  flex-shrink: 0;
  padding-left: 14px;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
  min-height: 0;
}

.cols-module {
  flex: 1;
  min-height: 0;
}

.rows-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  /* 三列等高，避免「大工业两部」字段较少时背景块比其他列矮 */
  align-items: stretch;
}

.rows-grid--in-top {
  /* 与右侧「字段所在列」同样参与拉伸，避免两边模块高度不一致 */
  flex: 1;
  min-height: 0;
}

.rows-module {
  min-width: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: #fafbfd;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  padding: 8px 8px 10px;
  overflow-x: hidden;
}

.rows-module.cols-module {
  overflow-x: visible;
}

.row-subtitle {
  font-size: 12px;
  font-weight: 600;
  color: rgba(46, 46, 46, 0.62);
  margin-bottom: 8px;
}

.options-grid--cols-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.options-grid--cols-stack :deep(.el-form-item) {
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.options-grid--cols-stack :deep(.el-form-item__label) {
  flex: 0 0 auto;
  width: 52px !important;
  padding: 0 4px 0 0 !important;
  justify-content: flex-end;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  color: rgba(46, 46, 46, 0.78);
}

.options-grid--cols-stack :deep(.el-form-item__content) {
  flex: 0 0 auto;
}

.options-grid--cols-stack :deep(.el-input-number) {
  width: 60px;
}

.options-grid--cols-stack :deep(.el-select.unit-select) {
  width: 60px;
  min-width: 60px;
}

.options-grid--cols-stack :deep(.unit-select .el-select__wrapper) {
  padding-left: 6px;
  padding-right: 18px;
}

.options-grid--cols-stack :deep(.el-input-number .el-input__wrapper),
.options-grid--cols-stack :deep(.el-select .el-select__wrapper) {
  border-radius: 6px;
}

.cols-title {
  font-size: 12px;
  font-weight: 600;
  color: rgba(46, 46, 46, 0.62);
  margin-bottom: 8px;
}

.recognize-panel--preview {
  padding-bottom: 16px;
}

.preview-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.preview-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.preview-panel {
  height: auto;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  overflow: hidden;
}

.preview-charts {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-chart-item {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  overflow: hidden;
}

.preview-chart-title {
  padding: 8px 10px;
  font-size: 12px;
  color: rgba(46, 46, 46, 0.68);
  background: rgba(58, 104, 255, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-chart {
  width: 100%;
  height: 220px;
}

.options-grid--rows {
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 6px;
}

/* 行号多为 1～2 位数字，输入框收窄；标签区固定，输入区不占满半屏 */
.options-grid--rows :deep(.el-input-number) {
  width: 60px;
  flex-shrink: 0;
}

.options-grid--rows :deep(.el-form-item) {
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0;
}

.options-grid--rows :deep(.el-form-item__label) {
  width: 52px !important;
  padding-right: 4px;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
}

.options-grid--rows :deep(.el-form-item__content) {
  flex: 0 0 auto;
  min-width: 0;
}

.options-grid--rows :deep(.el-input-number .el-input__wrapper) {
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 6px;
}

.options-grid--rows :deep(.el-input__inner) {
  text-align: center;
}

.tip-container {
  font-size: 12px;
  color: rgba(46, 46, 46, 0.55);
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.4;
}

@media (max-width: 1100px) {
  .main-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 960px) {
  .mapping-split {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .mapping-split__cols {
    width: 100%;
    max-width: 260px;
    padding-left: 0;
    border-left: none;
    padding-top: 12px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .cols-module {
    flex: 0 1 auto;
  }

  .rows-grid {
    grid-template-columns: repeat(3, minmax(150px, 1fr));
    overflow-x: auto;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;
  }

  .left-container {
    max-width: none;
  }
}
</style>
